'use strict';

import React, { Component } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import moment from 'moment';
import redirectURL from '../redirectURL';
import {
  COLORS,
  MULTI_SELECT_STYLES,
  safeArray,
  groupBy,
  distinctValues,
  sum,
  round1,
  baseChartOptions,
  baseAxisLine,
  baseValueAxis,
  GridCard,
} from './dashboardShared';
import './performanceDashboardModern.css';

/*
 * Inbound Dashboard - the 4th top-level dashboard, alongside Performance,
 * Transporter and FTL Movement. Sourced from the SAME
 * consignments/getDataForPowerBiDashBoard feed already powering Performance
 * and Transporter (server-side startDate/endDate filtering already works
 * correctly on this endpoint, unlike getInvoiceData/getFTLVehicleUtilization
 * - no client-side-only date workaround needed here).
 *
 * STATUS: there is no confirmed field/value that identifies a row as
 * "inbound" specifically within this feed - every row from the selected
 * date range is shown for now. Total CBM uses lr_total_cbm (total_cbm
 * exists but is frequently blank, same pattern as lr_total_weight vs
 * total_weight). Revised EDD uses revised_eta (confirmed from a real
 * sample response - revised_trip_end does not exist on this endpoint).
 *
 * "Unique Trip"/"Trips" matches the same definition used on the FTL
 * Movement tab: distinct (truck_no, dispatch-date) pairs, not a raw row
 * count - via dedupeByTrip below.
 */

const DATE_TYPE_OPTIONS = [
  { value: 'Dispatch', label: 'Dispatch Date' },
  { value: 'Invoice', label: 'Invoice Date' },
];

const MODE_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'FTL', label: 'FTL' },
  { value: 'PTL', label: 'PTL' },
];

function dispatchDateOf(row, dateType) {
  const value = dateType === 'Invoice' ? row.invoice_time : (row.dispatch_date || row.invoice_time);
  return value ? moment(value) : null;
}

function dayRange(start, end) {
  const days = [];
  let cursor = moment(start).startOf('day');
  const last = moment(end).startOf('day');
  while (cursor.isSameOrBefore(last)) {
    days.push(cursor.format('DD/MM/YYYY'));
    cursor = cursor.add(1, 'day');
  }
  return days;
}

function tripKeyOf(row, dateType) {
  const d = dispatchDateOf(row, dateType);
  return `${row.truck_no}||${d ? d.format('YYYY-MM-DD') : null}`;
}

// Trips = distinct (truck_no, dispatch-date) pairs, matching the FTL tab's
// own definition - not a raw row count. Respects the Date Basis toggle,
// same as the rest of this dashboard.
function dedupeByTrip(rows, dateType) {
  const seen = new Set();
  const result = [];
  rows.forEach((row) => {
    const key = tripKeyOf(row, dateType);
    if (!seen.has(key)) { seen.add(key); result.push(row); }
  });
  return result;
}

function weightKgOf(row) {
  const n = Number(row.lr_total_weight);
  return isNaN(n) ? 0 : n;
}

// lr_total_cbm, not total_cbm - same pattern as lr_total_weight vs
// total_weight (the plain total_ fields are frequently blank; the
// lr_-prefixed ones are the reliably populated real values).
function cbmOf(row) {
  const n = Number(row.lr_total_cbm);
  return isNaN(n) ? 0 : n;
}

export default class InboundDashboardModern extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: null,
      lastRefreshed: null,

      filters: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
        dateType: DATE_TYPE_OPTIONS[0],
        mode: MODE_OPTIONS[0],
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        destinationCity: [],
        destinationState: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
      },

      filterOptions: {
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        destinationCity: [],
        destinationState: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
      },

      rawRows: [],
      filteredRows: [],
      derived: null,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  buildPayload() {
    const f = this.state.filters;
    return {
      startDate: moment(f.startDate).format('YYYY-MM-DD'),
      endDate: moment(f.endDate).format('YYYY-MM-DD'),
      // Confirmed from the actual Flask source: getDataForPowerBiDashBoard
      // takes the short "dispatch"/"invoice" tokens, not the literal field
      // names - sending the literal names silently failed the backend's own
      // token check and left date_filter as None every time.
      date_filter: f.dateType.value === 'Invoice' ? 'invoice' : 'dispatch',
    };
  }

  loadData = () => {
    this.setState({ loading: true, error: null });
    redirectURL.post('consignments/getDataForPowerBiDashBoard', this.buildPayload())
      .then((resp) => {
        const rows = safeArray(resp.data);
        rows.forEach((row) => {
          row.movement_type_derived = /stock transfer/i.test(row.invoice_type || '') ? 'Primary' : 'Secondary';
        });
        const filterOptions = {
          entity: distinctValues(rows, 'truck_seal_entity').map((v) => ({ value: v, label: v })),
          movementType: distinctValues(rows, 'movement_type_derived').map((v) => ({ value: v, label: v })),
          sourceZone: distinctValues(rows, 'consigner_zone').map((v) => ({ value: v, label: v })),
          sourceCity: distinctValues(rows, 'consigner_city').map((v) => ({ value: v, label: v })),
          destinationZone: distinctValues(rows, 'consignee_zone').map((v) => ({ value: v, label: v })),
          destinationCity: distinctValues(rows, 'consignee_city').map((v) => ({ value: v, label: v })),
          destinationState: distinctValues(rows, 'consignee_state').map((v) => ({ value: v, label: v })),
          transporterCategory: distinctValues(rows, 'tpt_category').map((v) => ({ value: v, label: v })),
          invoiceType: distinctValues(rows, 'invoice_type').map((v) => ({ value: v, label: v })),
          transporter: distinctValues(rows, 'transporter_name').map((v) => ({ value: v, label: v })),
        };
        this.setState({ loading: false, lastRefreshed: moment(), rawRows: rows, filterOptions });
        this.applyClientFilters(rows);
      })
      .catch((error) => {
        console.error('Error fetching inbound dashboard data:', error);
        this.setState({ loading: false, error: 'Unable to load inbound dashboard data. Please try again.' });
      });
  };

  applyClientFilters = (rowsOverride) => {
    const rows = rowsOverride || this.state.rawRows;
    const f = this.state.filters;
    const matches = (row, selection, field) => {
      if (!selection.length) return true;
      return selection.map((o) => o.value).includes(row[field]);
    };
    const filtered = rows.filter((row) => (
      matches(row, f.entity, 'truck_seal_entity')
      && matches(row, f.movementType, 'movement_type_derived')
      && matches(row, f.sourceZone, 'consigner_zone')
      && matches(row, f.sourceCity, 'consigner_city')
      && matches(row, f.destinationZone, 'consignee_zone')
      && matches(row, f.destinationCity, 'consignee_city')
      && matches(row, f.destinationState, 'consignee_state')
      && matches(row, f.transporterCategory, 'tpt_category')
      && matches(row, f.invoiceType, 'invoice_type')
      && matches(row, f.transporter, 'transporter_name')
      && (f.mode.value === 'All' || row.tracking_type === f.mode.value)
    ));
    this.setState({ filteredRows: filtered, derived: this.computeDerived(filtered) });
  };

  computeDerived(rows) {
    const dateType = this.state.filters.dateType.value;
    const days = dayRange(this.state.filters.startDate, this.state.filters.endDate);
    const tripRows = dedupeByTrip(rows, dateType);

    // 1. Source Wise Unique Trip
    const sourceCityTrips = groupBy(tripRows, (r) => r.consigner_city || 'Unspecified');
    const sourceWiseTrips = Array.from(sourceCityTrips.entries())
      .map(([name, items]) => ({ name, trips: items.length }))
      .sort((a, b) => b.trips - a.trips);

    // 2. LR Wise Total Weight and CBM - row-level.
    const lrWiseRows = rows.map((r) => ({
      invoice_no: r.invoice_no,
      lr_no: r.lr_no,
      truck_no: r.truck_no,
      weightKg: weightKgOf(r),
      cbm: cbmOf(r),
    }));

    // 3. Trips Expected from Source - Source City x Day grid of unique trips.
    const sourceDayGroups = {};
    tripRows.forEach((r) => {
      const city = r.consigner_city || 'Unspecified';
      const d = dispatchDateOf(r, dateType);
      const day = d ? d.format('DD/MM/YYYY') : null;
      if (!day) return;
      if (!sourceDayGroups[city]) sourceDayGroups[city] = {};
      sourceDayGroups[city][day] = (sourceDayGroups[city][day] || 0) + 1;
    });
    const sourceCitiesForDayGrid = Object.keys(sourceDayGroups).sort();

    // 4. Trip Expected from Source - row-level detail. "Trips" is a 1/0 flag
    // (1 on a trip's first row, 0 on the rest) so the footer total is the
    // correct distinct trip count without double-counting.
    const seenTrip = new Set();
    const tripDetailRows = rows.map((r) => {
      const key = tripKeyOf(r, dateType);
      const isFirst = !seenTrip.has(key);
      seenTrip.add(key);
      return { ...r, __tripFlag: isFirst ? 1 : 0, weightKg: weightKgOf(r), cbm: cbmOf(r) };
    });

    return {
      totalRecords: rows.length,
      totalTrips: tripRows.length,
      sourceWiseTrips,
      lrWiseRows,
      days,
      sourceDayGroups,
      sourceCitiesForDayGrid,
      tripDetailRows,
    };
  }

  onFilterChange = (key, value) => {
    this.setState((prev) => ({ filters: { ...prev.filters, [key]: value } }));
  };

  onApply = () => { this.loadData(); };

  onReset = () => {
    this.setState({
      filters: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
        dateType: DATE_TYPE_OPTIONS[0],
        mode: MODE_OPTIONS[0],
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        destinationCity: [],
        destinationState: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
      },
    }, () => this.loadData());
  };

  renderEmptyState(message, hint) {
    return (
      <div className="pdm-empty-state">
        <div className="pdm-empty-state-icon">&#9679;</div>
        <strong>{message}</strong>
        {hint && <span>{hint}</span>}
      </div>
    );
  }

  renderChartCard(title, subtitle, options, height = 320) {
    return (
      <div className="pdm-card">
        {title && <p className="pdm-card-title">{title}</p>}
        {subtitle && <p className="pdm-card-subtitle">{subtitle}</p>}
        {options ? (
          <div className="pdm-chart-flex" style={{ height }}>
            <HighchartsReact
              highcharts={Highcharts}
              containerProps={{ style: { width: '100%', height: '100%' } }}
              options={{ ...options, chart: { ...options.chart, height } }}
              updateArgs={[true, true, true]}
            />
          </div>
        ) : this.renderEmptyState('No data available', 'Try widening the date range or clearing a filter.')}
      </div>
    );
  }

  buildSourceWiseTripsOptions(sourceWiseTrips) {
    if (!sourceWiseTrips.length) return null;
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'column' },
      title: { text: null },
      xAxis: baseAxisLine(sourceWiseTrips.map((s) => s.name)),
      yAxis: baseValueAxis('Unique Trips'),
      legend: { enabled: false },
      plotOptions: { series: { borderRadius: 4, borderWidth: 0, colorByPoint: true, dataLabels: { enabled: true, style: { fontSize: '10px', color: COLORS.ink, textOutline: 'none' } } } },
      tooltip: { headerFormat: '', pointFormat: '<b>{point.key}</b><br/>Unique Trips: <b>{point.y}</b>' },
      series: [{ name: 'Unique Trips', data: sourceWiseTrips.map((s) => s.trips), color: COLORS.blue }],
    };
  }

  buildLrWiseGrid(d) {
    const columnDefs = [
      { headerName: 'Invoice No', field: 'invoice_no', pinned: 'left', width: 150 },
      { headerName: 'LR No', field: 'lr_no', width: 130 },
      { headerName: 'Truck No', field: 'truck_no', width: 130 },
      { headerName: 'Total Weight (KG)', field: 'weightKg', width: 150 },
      { headerName: 'Total CBM', field: 'cbm', width: 130 },
    ];
    const pinnedBottomRowData = [{
      invoice_no: 'Total',
      weightKg: round1(sum(d.lrWiseRows.map((r) => r.weightKg))),
      cbm: round1(sum(d.lrWiseRows.map((r) => r.cbm))),
    }];
    return { columnDefs, records: d.lrWiseRows, pinnedBottomRowData };
  }

  buildTripsExpectedDayGrid(d) {
    const records = d.sourceCitiesForDayGrid.map((city) => {
      const row = { sourceCity: city };
      d.days.forEach((day) => { row[day] = d.sourceDayGroups[city][day] || 0; });
      return row;
    });
    const columnDefs = [
      { headerName: 'Source City', field: 'sourceCity', pinned: 'left', width: 180 },
      ...d.days.map((day) => ({ headerName: day, field: day, width: 110 })),
    ];
    const colTotals = { sourceCity: 'Total' };
    d.days.forEach((day) => { colTotals[day] = sum(records.map((r) => r[day])); });
    return { columnDefs, records, pinnedBottomRowData: [colTotals] };
  }

  buildTripDetailColumnDefs() {
    return [
      { headerName: 'Source City', field: 'consigner_city', pinned: 'left', width: 140 },
      { headerName: 'Transporter Name', field: 'transporter_name', width: 180 },
      { headerName: 'Vehicle Type', field: 'truck_type', width: 140 },
      { headerName: 'Truck No', field: 'truck_no', width: 130 },
      { headerName: 'Dispatch Date', field: 'dispatch_date', width: 150, valueGetter: (p) => (p.data.dispatch_date ? moment(p.data.dispatch_date).format('DD-MMM-YYYY') : '') },
      { headerName: 'Expected ETA', field: 'expected_eta', width: 160, valueGetter: (p) => (p.data.expected_eta ? moment(p.data.expected_eta).format('DD-MMM-YYYY HH:mm') : '') },
      { headerName: 'Revised EDD', field: 'revised_eta', width: 160, valueGetter: (p) => (p.data.revised_eta ? moment(p.data.revised_eta).format('DD-MMM-YYYY HH:mm') : '') },
      { headerName: 'Total Weight (KG)', field: 'weightKg', width: 160 },
      { headerName: 'Total CBM', field: 'cbm', width: 120 },
      { headerName: 'Trips', field: '__tripFlag', width: 90, headerTooltip: '1 on the first row of each distinct vehicle+dispatch-date trip, 0 on the rest, so the total below is the correct distinct trip count.' },
    ];
  }

  render() {
    const { state } = this;
    const f = state.filters;
    const fo = state.filterOptions;
    const d = state.derived;

    return (
      <>
        {state.loading && (
          <div className="pdm-loading-overlay"><div className="pdm-spinner" /></div>
        )}
        {state.error && <div className="pdm-error-banner">{state.error}</div>}

        <div className="pdm-filterbar">
          <div className="pdm-field">
            <label className="pdm-filter-required">From</label>
            <DatePicker className="pdm-datepicker" selected={f.startDate} onChange={(v) => this.onFilterChange('startDate', v)} dateFormat="dd/MM/yyyy" maxDate={f.endDate} />
          </div>
          <div className="pdm-field">
            <label className="pdm-filter-required">To</label>
            <DatePicker className="pdm-datepicker" selected={f.endDate} onChange={(v) => this.onFilterChange('endDate', v)} dateFormat="dd/MM/yyyy" minDate={f.startDate} maxDate={new Date()} />
          </div>
          <div className="pdm-field">
            <label>Date Basis</label>
            <Select styles={MULTI_SELECT_STYLES} value={f.dateType} options={DATE_TYPE_OPTIONS} onChange={(v) => this.onFilterChange('dateType', v)} />
          </div>
          <div className="pdm-field">
            <label>Mode</label>
            <Select styles={MULTI_SELECT_STYLES} value={f.mode} options={MODE_OPTIONS} onChange={(v) => this.onFilterChange('mode', v)} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Entity</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.entity} options={fo.entity} onChange={(v) => this.onFilterChange('entity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Movement Type</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.movementType} options={fo.movementType} onChange={(v) => this.onFilterChange('movementType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Source Zone</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.sourceZone} options={fo.sourceZone} onChange={(v) => this.onFilterChange('sourceZone', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Source City</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.sourceCity} options={fo.sourceCity} onChange={(v) => this.onFilterChange('sourceCity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Destination Zone</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.destinationZone} options={fo.destinationZone} onChange={(v) => this.onFilterChange('destinationZone', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Destination City</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.destinationCity} options={fo.destinationCity} onChange={(v) => this.onFilterChange('destinationCity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Destination State</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.destinationState} options={fo.destinationState} onChange={(v) => this.onFilterChange('destinationState', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Transporter Category</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.transporterCategory} options={fo.transporterCategory} onChange={(v) => this.onFilterChange('transporterCategory', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Invoice Type</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.invoiceType} options={fo.invoiceType} onChange={(v) => this.onFilterChange('invoiceType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Transporter</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.transporter} options={fo.transporter} onChange={(v) => this.onFilterChange('transporter', v || [])} />
          </div>
          <div className="pdm-filter-toolbar">
            <button className="pdm-btn pdm-btn-ghost" onClick={this.onReset}>Reset</button>
            <button className="pdm-btn pdm-btn-primary" onClick={this.onApply}>Apply Filters</button>
          </div>
        </div>

        {!d ? (
          <div className="pdm-card">{this.renderEmptyState('Loading inbound dashboard...', null)}</div>
        ) : (
          <>
            <div className="pdm-notice-banner">
              No confirmed field identifies these records as specifically "inbound" yet - showing all shipment records for the selected filters.
            </div>

            <div className="pdm-kpi-grid">
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">Records (period)</div>
                <div className="pdm-kpi-value">{d.totalRecords}</div>
                <div className="pdm-kpi-sub">Current filters</div>
              </div>
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">Unique Trips (period)</div>
                <div className="pdm-kpi-value">{d.totalTrips}</div>
                <div className="pdm-kpi-sub">Distinct vehicle + dispatch-date combinations</div>
              </div>
            </div>

            <div className="pdm-section">
              {this.renderChartCard(
                'Source Wise Unique Trip',
                'Unique trips per source city.',
                this.buildSourceWiseTripsOptions(d.sourceWiseTrips),
                340,
              )}
            </div>

            <div className="pdm-section">
              <div className="pdm-section-head">
                <div>
                  <h2>LR Wise Total Weight and CBM</h2>
                  <p>Invoice No, LR No, Truck No, Total Weight (KG), Total CBM - with totals below.</p>
                </div>
              </div>
              <GridCard title="LR Wise Total Weight and CBM" loading={state.loading} onRefresh={this.loadData} height={480} {...this.buildLrWiseGrid(d)} />
            </div>

            <div className="pdm-section">
              <div className="pdm-section-head">
                <div>
                  <h2>Trips Expected from Source</h2>
                  <p>Source City x day, unique trips per day - with totals below.</p>
                </div>
              </div>
              <GridCard title="Trips Expected from Source" loading={state.loading} onRefresh={this.loadData} height={480} {...this.buildTripsExpectedDayGrid(d)} />
            </div>

            <div className="pdm-section">
              <div className="pdm-section-head">
                <div>
                  <h2>Trip Expected from Source</h2>
                  <p>Source City, Transporter, Vehicle Type, Truck No, Dispatch Date, Expected ETA, Revised EDD, Total Weight, Total CBM, Trips - with totals for Weight and Trips below.</p>
                </div>
              </div>
              <GridCard
                title="Trip Expected from Source"
                loading={state.loading}
                onRefresh={this.loadData}
                height={600}
                records={d.tripDetailRows}
                columnDefs={this.buildTripDetailColumnDefs()}
                pinnedBottomRowData={[{
                  consigner_city: 'Total',
                  weightKg: round1(sum(d.tripDetailRows.map((r) => r.weightKg))),
                  __tripFlag: sum(d.tripDetailRows.map((r) => r.__tripFlag)),
                }]}
              />
            </div>
          </>
        )}
      </>
    );
  }
}
